import React, { useState, useMemo, useEffect } from "react";
import {
    Button,
    Box,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Typography,
    Grid,
    Paper,
    TextField,
    Switch,
    FormControlLabel,
} from "@mui/material";
import { ReactGrid } from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";

const EXCEL_EXPORT_FIELDS = [
    { key: "custom_id", display_name: "Line Id" },
    { key: "name", display_name: "Line Name (Description)" },
    { key: "is_internal", display_name: "Is Internal" },
    { key: "vendor_currency", display_name: "Vendor Currency" },
    { key: "vendor_rate", display_name: "Vendor Rate" },
    { key: "rate_type", display_name: "Rate Type" },
    {
        key: "vendor_client_exchange_rate_buffered",
        display_name: "FX (Buffered)",
    },
    { key: "days", display_name: "Qty (Days)" },
    { key: "quantity", display_name: "Units" },
    { key: "ot_1_5", display_name: "OT 1.5" },
    { key: "ot_2_0", display_name: "OT 2.0" },
    { key: "markup", display_name: "Markup" },
    { key: "adjustment", display_name: "Adjustment" },
    { key: "external_notes", display_name: "External Notes" },
    { key: "internal_notes", display_name: "Internal Notes" },
];

export default function CustomBidImportWizard({
    previewData,
    initialConfig,
    onPrimarySubmit,
    onSecondarySubmit,
    primarySubmitText = "Run Smart Import",
    secondarySubmitText,
    onCancel,
    isImporting,
}) {
    const sheets = previewData?.sheets || [];
    const [selectedSheets, setSelectedSheets] = useState(
        initialConfig?.sheet_names || sheets,
    );
    const [tabsAsSections, setTabsAsSections] = useState(
        initialConfig?.tab_names_as_sections || false,
    );
    const [headerRowIndex, setHeaderRowIndex] = useState(
        initialConfig?.header_row_index || 0,
    );
    const [dataStartRowIndex, setDataStartRowIndex] = useState(
        initialConfig?.data_start_row_index || 1,
    );

    // mapping: { "0": "description", "1": "quantity" }
    const [columnMapping, setColumnMapping] = useState(
        initialConfig?.column_mapping || {},
    );

    const [hierarchyType, setHierarchyType] = useState(
        initialConfig?.hierarchy?.type || "flat",
    ); // "flat" | "single_column" | "multi_column"
    const [groupColumnIndex, setGroupColumnIndex] = useState(
        initialConfig?.hierarchy?.single_column_rule?.group_column_index || 0,
    );
    const [sectionColumnIndex, setSectionColumnIndex] = useState(
        initialConfig?.hierarchy?.multi_column_rule?.section_column_index || "",
    );
    const [categoryColumnIndex, setCategoryColumnIndex] = useState(
        initialConfig?.hierarchy?.multi_column_rule?.category_column_index ||
            "",
    );

    useEffect(() => {
        if (initialConfig) {
            if (initialConfig.sheet_names)
                setSelectedSheets(initialConfig.sheet_names);
            if (initialConfig.tab_names_as_sections !== undefined)
                setTabsAsSections(initialConfig.tab_names_as_sections);
            if (initialConfig.header_row_index !== undefined)
                setHeaderRowIndex(initialConfig.header_row_index);
            if (initialConfig.data_start_row_index !== undefined)
                setDataStartRowIndex(initialConfig.data_start_row_index);
            if (initialConfig.column_mapping)
                setColumnMapping(initialConfig.column_mapping);

            if (initialConfig.hierarchy) {
                setHierarchyType(initialConfig.hierarchy.type || "flat");
                if (initialConfig.hierarchy.single_column_rule) {
                    setGroupColumnIndex(
                        initialConfig.hierarchy.single_column_rule
                            .group_column_index || 0,
                    );
                }
                if (initialConfig.hierarchy.multi_column_rule) {
                    setSectionColumnIndex(
                        initialConfig.hierarchy.multi_column_rule
                            .section_column_index !== undefined
                            ? initialConfig.hierarchy.multi_column_rule
                                  .section_column_index
                            : "",
                    );
                    setCategoryColumnIndex(
                        initialConfig.hierarchy.multi_column_rule
                            .category_column_index !== undefined
                            ? initialConfig.hierarchy.multi_column_rule
                                  .category_column_index
                            : "",
                    );
                }
            }
        }
    }, [initialConfig]);

    const activePreviewSheet =
        selectedSheets.length > 0 ? selectedSheets[0] : sheets[0] || "";
    const sheetData = previewData?.preview_data?.[activePreviewSheet] || [];

    const maxCols = useMemo(() => {
        if (!sheetData.length) return 0;
        return Math.max(...sheetData.map((r) => r.length));
    }, [sheetData]);

    const handleMapChange = (colIndex, fieldKey) => {
        setColumnMapping((prev) => {
            const newMap = { ...prev };
            if (!fieldKey) {
                delete newMap[colIndex];
            } else {
                newMap[colIndex] = fieldKey;
            }
            return newMap;
        });
    };

    const isMissingRequired = !Object.values(columnMapping).includes("name");

    const getConfig = () => {
        return {
            sheet_names: selectedSheets,
            tab_names_as_sections: tabsAsSections,
            header_row_index: headerRowIndex,
            data_start_row_index: dataStartRowIndex,
            column_mapping: columnMapping,
            hierarchy: {
                type: hierarchyType,
                single_column_rule:
                    hierarchyType === "single_column"
                        ? {
                              group_column_index: groupColumnIndex,
                              empty_columns_required: Object.keys(columnMapping)
                                  .filter((k) =>
                                      [
                                          "quantity",
                                          "vendor_rate",
                                          "markup",
                                          "days",
                                      ].includes(columnMapping[k]),
                                  )
                                  .map(Number),
                          }
                        : {},
                multi_column_rule:
                    hierarchyType === "multi_column"
                        ? {
                              section_column_index: sectionColumnIndex,
                              category_column_index: categoryColumnIndex,
                          }
                        : {},
                default_section_name: "Imported Section",
                default_category_name: "Imported Category",
            },
        };
    };

    const handlePrimarySubmit = () =>
        onPrimarySubmit && onPrimarySubmit(getConfig());
    const handleSecondarySubmit = () =>
        onSecondarySubmit && onSecondarySubmit(getConfig());

    const getColumns = () => {
        const cols = [{ columnId: "row_label", width: 100 }];
        for (let i = 0; i < maxCols; i++) {
            cols.push({ columnId: i.toString(), width: 150 });
        }
        return cols;
    };

    const getRows = () => {
        const rows = [];

        // Header Mapping row visual only
        const mappingCells = [{ type: "header", text: "Map Fields ->" }];
        for (let col = 0; col < maxCols; col++) {
            const currentMap = columnMapping[col] || "Unmapped";
            mappingCells.push({ type: "header", text: currentMap });
        }
        rows.push({ rowId: "mapping_header", cells: mappingCells });

        sheetData.forEach((r, idx) => {
            let rowLabel = `Row ${idx}`; // User visible 0-index match
            if (idx === headerRowIndex) rowLabel = "HEADERS ►";
            if (idx === dataStartRowIndex) rowLabel = "DATA START ►";

            const cells = [{ type: "header", text: rowLabel }];
            for (let c = 0; c < maxCols; c++) {
                cells.push({
                    type: "text",
                    text: r[c] ? r[c].toString() : "",
                    nonEditable: true,
                });
            }
            rows.push({
                rowId: idx,
                cells,
            });
        });
        return rows;
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
                bgcolor: "background.default",
            }}
        >
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    p: 4,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                }}
            >
                <Typography variant="h5" fontWeight="800">
                    Smart Import Wizard
                </Typography>

                <Paper
                    variant="outlined"
                    sx={{ p: 3, borderRadius: 2, bgcolor: "background.paper" }}
                >
                    <Typography
                        variant="subtitle1"
                        fontWeight="700"
                        gutterBottom
                    >
                        1. Configuration & Boundaries
                    </Typography>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6} md={6}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Target Sheets</InputLabel>
                                <Select
                                    multiple
                                    value={selectedSheets}
                                    onChange={(e) =>
                                        setSelectedSheets(
                                            typeof e.target.value === "string"
                                                ? e.target.value.split(",")
                                                : e.target.value,
                                        )
                                    }
                                    label="Target Sheets"
                                >
                                    {sheets.map((s) => (
                                        <MenuItem key={s} value={s}>
                                            {s}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={6}
                            sx={{ display: "flex", alignItems: "center" }}
                        >
                            <Paper
                                variant="outlined"
                                sx={{ width: "100%", pl: 2 }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={tabsAsSections}
                                            onChange={(e) =>
                                                setTabsAsSections(
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                    }
                                    label="Tabs as Sections"
                                />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                size="small"
                                fullWidth
                                type="number"
                                label="Header Row (0-indexed)"
                                value={headerRowIndex}
                                onChange={(e) =>
                                    setHeaderRowIndex(parseInt(e.target.value))
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                size="small"
                                fullWidth
                                type="number"
                                label="Data Start Row (0-indexed)"
                                value={dataStartRowIndex}
                                onChange={(e) =>
                                    setDataStartRowIndex(
                                        parseInt(e.target.value),
                                    )
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Hierarchy Grouping</InputLabel>
                                <Select
                                    value={hierarchyType}
                                    onChange={(e) =>
                                        setHierarchyType(e.target.value)
                                    }
                                    label="Hierarchy Grouping"
                                >
                                    <MenuItem value="flat">
                                        Flat (No Groups)
                                    </MenuItem>
                                    <MenuItem value="single_column">
                                        Groups in Same Column
                                    </MenuItem>
                                    <MenuItem value="multi_column">
                                        Groups in Different Columns
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {hierarchyType === "single_column" && (
                        <Grid
                            container
                            spacing={3}
                            sx={{ mt: 1, alignItems: "center" }}
                        >
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    label="Group Col Index (Category Name)"
                                    value={groupColumnIndex}
                                    onChange={(e) =>
                                        setGroupColumnIndex(
                                            parseInt(e.target.value),
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    A row will be categorized as a
                                    Section/Category if this column has text,
                                    and all mapped quantity/cost columns are
                                    empty.
                                </Typography>
                            </Grid>
                        </Grid>
                    )}

                    {hierarchyType === "multi_column" && (
                        <Grid
                            container
                            spacing={3}
                            sx={{ mt: 1, alignItems: "center" }}
                        >
                            {!tabsAsSections && (
                                <Grid item xs={12} sm={6}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>
                                            Section Column Index (Optional)
                                        </InputLabel>
                                        <Select
                                            value={sectionColumnIndex}
                                            onChange={(e) =>
                                                setSectionColumnIndex(
                                                    e.target.value,
                                                )
                                            }
                                            label="Section Column Index (Optional)"
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {Array.from({
                                                length: maxCols,
                                            }).map((_, c) => (
                                                <MenuItem key={c} value={c}>
                                                    Col {c}{" "}
                                                    {sheetData[
                                                        headerRowIndex
                                                    ]?.[c]
                                                        ? `[${sheetData[headerRowIndex][c]}]`
                                                        : ""}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid item xs={12} sm={tabsAsSections ? 12 : 6}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>
                                        Category Column Index (Optional)
                                    </InputLabel>
                                    <Select
                                        value={categoryColumnIndex}
                                        onChange={(e) =>
                                            setCategoryColumnIndex(
                                                e.target.value,
                                            )
                                        }
                                        label="Category Column Index (Optional)"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {Array.from({ length: maxCols }).map(
                                            (_, c) => (
                                                <MenuItem key={c} value={c}>
                                                    Col {c}{" "}
                                                    {sheetData[
                                                        headerRowIndex
                                                    ]?.[c]
                                                        ? `[${sheetData[headerRowIndex][c]}]`
                                                        : ""}
                                                </MenuItem>
                                            ),
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}
                </Paper>

                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                        bgcolor: "background.paper",
                        minHeight: 400,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        fontWeight="700"
                        sx={{ mb: 1 }}
                    >
                        2. Column Mappings & Preview
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 3 }}
                    >
                        Map the columns below to your project's field types.
                        Verify your target sheet's raw data in the grid to
                        ensure alignment.
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            gap: 1.5,
                            overflowX: "auto",
                            pt: 1,
                            pb: 2,
                            width: "100%",
                            flexShrink: 0,
                        }}
                    >
                        {Array.from({ length: maxCols }).map((_, c) => {
                            const rawHeader = sheetData[headerRowIndex]?.[c];
                            const headerName = rawHeader
                                ? `[${rawHeader}]`
                                : `Col ${c}`;
                            return (
                                <FormControl
                                    key={c}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                >
                                    <InputLabel>{headerName} Map To</InputLabel>
                                    <Select
                                        value={columnMapping[c] || ""}
                                        onChange={(e) =>
                                            handleMapChange(c, e.target.value)
                                        }
                                        label={`${headerName} Map To`}
                                    >
                                        <MenuItem value="">
                                            <em>Ignore</em>
                                        </MenuItem>
                                        {EXCEL_EXPORT_FIELDS.map((f) => (
                                            <MenuItem key={f.key} value={f.key}>
                                                {f.display_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                        })}
                    </Box>

                    <Box
                        sx={{
                            minHeight: 0,
                            flexGrow: 1,
                            overflow: "auto",
                            position: "relative",
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            "& .reactgrid-content .rg-pane .rg-cell": {
                                color: "text.primary",
                                backgroundColor: "background.paper",
                            },
                            "& .reactgrid-content .rg-pane .rg-cell.rg-header-cell":
                                {
                                    color: "text.primary",
                                    backgroundColor: "background.default",
                                },
                        }}
                    >
                        <ReactGrid
                            rows={getRows()}
                            columns={getColumns()}
                            enableRowSelection
                            onActiveCellChange={(cell) => {
                                if (typeof cell.rowId === "number") {
                                    if (
                                        window.confirm(
                                            `Set row ${cell.rowId} as the Header Row and row ${cell.rowId + 1} as Data Start row?`,
                                        )
                                    ) {
                                        setHeaderRowIndex(cell.rowId);
                                        setDataStartRowIndex(cell.rowId + 1);
                                    }
                                }
                            }}
                        />
                    </Box>
                </Paper>
            </Box>

            <Box
                sx={{
                    p: 2,
                    px: 4,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    borderRadius: 0,
                    zIndex: 10,
                }}
            >
                {isMissingRequired && (
                    <Typography
                        variant="body2"
                        color="error"
                        sx={{ alignSelf: "center", mr: 2, fontWeight: "bold" }}
                    >
                        * You must map at least one column to 'Line Name' to
                        import.
                    </Typography>
                )}
                <Button
                    variant="outlined"
                    color="inherit"
                    disabled={isImporting}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                {onSecondarySubmit && (
                    <Button
                        variant="outlined"
                        color="primary"
                        disabled={isImporting || isMissingRequired}
                        onClick={handleSecondarySubmit}
                    >
                        {secondarySubmitText || "Secondary Action"}
                    </Button>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isImporting || isMissingRequired}
                    onClick={handlePrimarySubmit}
                    size="large"
                    sx={{ fontWeight: "bold", px: 4 }}
                >
                    {isImporting ? "Importing..." : primarySubmitText}
                </Button>
            </Box>
        </Box>
    );
}
